import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ProjectService } from '../../../services/project.service';
import { Project } from '../../../models/project.model';
import { transformMediaUrl, onImageError } from '../../../utils/media-url.util';

export interface AdminUserItem {
  id: number;
  username: string;
  created_at: string;
}

export interface ConsultationItem {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  project_type: string;
  suburb: string;
  budget_range: string;
  message: string;
  reference_code: string;
  created_at: string;
}

const USERS_STORAGE_KEY = 'ambition_admin_users_list_v2';
const AUTH_STORAGE_KEY = 'ambition_local_auth';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.scss'
})
export class AdminDashboardComponent implements OnInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private projectService = inject(ProjectService);

  projects = this.projectService.projects;
  activeTab = signal<'projects' | 'users' | 'consultations'>('projects');

  isEditing = signal<boolean>(false);
  editingProjectId = signal<string | null>(null);
  deletingProject = signal<{ id: string, title: string } | null>(null);

  // File Uploading & Loading Signals
  videoFileName = signal<string>('');
  coverFileName = signal<string>('');
  isCoverUploading = signal<boolean>(false);
  isVideoUploading = signal<boolean>(false);
  isGalleryUploading = signal<boolean>(false);

  // Real Server Uploaded URLs (Non-blob)
  serverCoverUrl = signal<string>('');
  serverVideoUrl = signal<string>('');

  onImgErr = onImageError;
  transformUrl = transformMediaUrl;

  // Admin User Management State
  adminUsersList = signal<AdminUserItem[]>([]);
  createUserSuccess = signal<string>('');
  createUserError = signal<string>('');

  // Consultation Inquiries List
  consultationList = signal<ConsultationItem[]>([]);

  // Tag lists for features, materials, and gallery photos
  featuresList = signal<string[]>([]);
  materialsList = signal<string[]>([]);
  galleryList = signal<string[]>([]);

  projectForm: FormGroup = this.fb.group({
    title: ['', Validators.required],
    category: ['custom', Validators.required],
    categoryLabel: ['Custom Residence', Validators.required],
    location: ['', Validators.required],
    suburb: ['', Validators.required],
    image: ['', Validators.required],
    description: ['', Validators.required],
    fullOverview: ['', Validators.required],
    buildTime: ['12 Months', Validators.required],
    size: ['500 sqm', Validators.required],
    completionYear: ['2024', Validators.required],
    architect: ['Ambition Built Architectural Studio', Validators.required],
    videoUrl: [''],
    newFeatureInput: [''],
    newMaterialInput: [''],
    newGalleryInput: ['']
  });

  userForm: FormGroup = this.fb.group({
    username: ['', [Validators.required, Validators.minLength(3)]],
    password: ['', [Validators.required, Validators.minLength(4)]]
  });

  ngOnInit() {
    this.checkAdminAuth();
    this.fetchAdminUsers();
    this.fetchConsultations();
    this.projectService.fetchProjectsFromBackend();
  }

  // --- TAB & AUTHENTICATION NAVIGATION ---
  setTab(tab: 'projects' | 'users' | 'consultations') {
    this.activeTab.set(tab);
    if (tab === 'projects') {
      this.projectService.fetchProjectsFromBackend();
    } else if (tab === 'users') {
      this.fetchAdminUsers();
    } else if (tab === 'consultations') {
      this.fetchConsultations();
    }
  }

  checkAdminAuth() {
    if (typeof window !== 'undefined' && window.localStorage) {
      const isAuth = localStorage.getItem('ambition_local_auth');
      const token = localStorage.getItem('ambition_admin_token');
      if (isAuth !== 'true' && !token) {
        this.router.navigate(['/admin/login']);
      }
    }
  }

  logout() {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('ambition_local_auth');
      localStorage.removeItem('ambition_admin_token');
    }
    this.router.navigate(['/admin/login']);
  }

  // --- CONSULTATIONS LEAD MANAGEMENT ---
  fetchConsultations() {
    let localConsultations: ConsultationItem[] = [];
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('ambition_consultations_list_v1');
      if (saved) {
        try { localConsultations = JSON.parse(saved); } catch (e) { }
      }
    }

    fetch('/api/consultations.php')
      .then(res => res.json())
      .then(resData => {
        if (resData.status === 'success' && Array.isArray(resData.data) && resData.data.length > 0) {
          this.consultationList.set(resData.data);
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('ambition_consultations_list_v1', JSON.stringify(resData.data));
          }
        } else {
          this.consultationList.set(localConsultations);
        }
      })
      .catch(() => {
        this.consultationList.set(localConsultations);
      });
  }

  // --- ADMIN USER MANAGEMENT METHODS ---
  private loadLocalUsers(): AdminUserItem[] {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem(USERS_STORAGE_KEY);
      if (saved) {
        try { return JSON.parse(saved); } catch (e) { }
      }
    }
    return [
      { id: 1, username: 'admin', created_at: 'Master Account' }
    ];
  }

  fetchAdminUsers() {
    const localUsers = this.loadLocalUsers();

    fetch('/api/users.php')
      .then(res => {
        if (!res.ok) throw new Error('API Notice');
        return res.json();
      })
      .then(resData => {
        if (resData.status === 'success' && Array.isArray(resData.data) && resData.data.length > 0) {
          this.adminUsersList.set(resData.data);
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(resData.data));
          }
        } else {
          this.adminUsersList.set(localUsers);
        }
      })
      .catch(() => {
        this.adminUsersList.set(localUsers);
      });
  }

  createAdminUser() {
    if (this.userForm.invalid) {
      this.createUserError.set('Username (3+ chars) & Password (4+ chars) required.');
      return;
    }

    const { username, password } = this.userForm.value;
    this.createUserSuccess.set('');
    this.createUserError.set('');

    fetch('/api/users.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    })
      .then(res => res.json())
      .then(resData => {
        if (resData.status === 'success') {
          this.createUserSuccess.set(`Admin user "${username}" created successfully.`);
          this.userForm.reset();
          this.fetchAdminUsers();
        } else {
          this.createUserError.set(resData.message || 'Failed to create user');
        }
      })
      .catch(() => {
        const currentUsers = this.loadLocalUsers();
        const newUser: AdminUserItem = {
          id: Date.now(),
          username: username,
          created_at: new Date().toLocaleDateString()
        };
        const updated = [newUser, ...currentUsers];
        this.adminUsersList.set(updated);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(updated));
        }
        this.createUserSuccess.set(`Admin user "${username}" created locally.`);
        this.userForm.reset();
      });
  }

  deleteAdminUser(id: number, username?: string) {
    const updated = this.adminUsersList().filter(u => u.id !== id);
    this.adminUsersList.set(updated);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(updated));
    }

    fetch(`/api/users.php?id=${id}`, {
      method: 'DELETE'
    }).catch(() => { });
  }

  // --- PROJECT CRUD METHODS ---
  openCreateModal() {
    this.editingProjectId.set(null);
    this.videoFileName.set('');
    this.coverFileName.set('');
    this.serverCoverUrl.set('');
    this.serverVideoUrl.set('');
    this.isCoverUploading.set(false);
    this.isVideoUploading.set(false);
    this.isGalleryUploading.set(false);

    this.featuresList.set(['Rimless Infinity Pool', 'Double Glazed Low-E Glass', 'Butler Pantry']);
    this.materialsList.set(['Calacatta Marble', 'Burnished Concrete', 'Hardwood Timber']);
    this.galleryList.set([]);

    this.projectForm.reset({
      title: '',
      category: 'custom',
      categoryLabel: 'Custom Residence',
      location: '',
      suburb: '',
      image: '',
      description: '',
      fullOverview: '',
      buildTime: '12 Months',
      size: '500 sqm',
      completionYear: new Date().getFullYear().toString(),
      architect: 'Ambition Built Architectural Studio',
      videoUrl: ''
    });

    this.isEditing.set(true);
  }

  openEditModal(p: Project) {
    this.editingProjectId.set(p.id);
    this.videoFileName.set(p.videoUrl ? 'Existing Video Attached' : '');
    this.coverFileName.set(p.image ? 'Existing Cover Image' : '');
    this.serverCoverUrl.set(p.image || '');
    this.serverVideoUrl.set(p.videoUrl || '');
    this.isCoverUploading.set(false);
    this.isVideoUploading.set(false);
    this.isGalleryUploading.set(false);

    this.featuresList.set([...p.features]);
    this.materialsList.set([...p.materials]);
    this.galleryList.set([...p.galleryImages]);

    this.projectForm.patchValue({
      title: p.title,
      category: p.category,
      categoryLabel: p.categoryLabel,
      location: p.location,
      suburb: p.suburb,
      image: p.image,
      description: p.description,
      fullOverview: p.fullOverview,
      buildTime: p.buildTime,
      size: p.size,
      completionYear: p.completionYear,
      architect: p.architect,
      videoUrl: p.videoUrl || ''
    });

    this.isEditing.set(true);
  }

  closeModal() {
    this.isEditing.set(false);
    this.editingProjectId.set(null);
  }

  confirmDelete(p: Project) {
    this.deletingProject.set({ id: p.id, title: p.title });
  }

  cancelDelete() {
    this.deletingProject.set(null);
  }

  executeDelete() {
    const target = this.deletingProject();
    if (target) {
      this.projectService.deleteProject(target.id);
      this.deletingProject.set(null);
    }
  }

  // --- FAST & COMPRESSED FILE UPLOADER HANDLERS ---
  private getTargetProjectId(): string {
    const editId = this.editingProjectId();
    if (editId) return editId;
    const titleVal = this.projectForm.get('title')?.value?.trim();
    if (titleVal) return pregId(titleVal);
    return 'proj-' + Date.now();
  }

  private uploadFileToBackend(file: File, projectId: string, callback: (url: string) => void) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);

    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const uploadUrl = isLocalhost ? 'https://demo.ambitionbuilt.com.au/api/upload.php' : '/api/upload.php';

    fetch(uploadUrl, {
      method: 'POST',
      body: formData
    })
      .then(res => {
        if (!res.ok) throw new Error('Upload error status ' + res.status);
        return res.json();
      })
      .then(resData => {
        if (resData.status === 'success' && resData.url) {
          callback(resData.url);
        } else {
          console.warn('Upload error:', resData.message);
        }
      })
      .catch(err => {
        console.warn('Media upload warning:', err);
      });
  }

  async onCoverFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.coverFileName.set(file.name);
      this.isCoverUploading.set(true);
      const projId = this.getTargetProjectId();

      // Instant local preview
      const localPreviewUrl = URL.createObjectURL(file);
      this.projectForm.patchValue({ image: localPreviewUrl });

      this.uploadFileToBackend(file, projId, (serverUrl) => {
        this.serverCoverUrl.set(serverUrl);
        this.projectForm.patchValue({ image: serverUrl });
        this.isCoverUploading.set(false);
      });

      setTimeout(() => {
        this.isCoverUploading.set(false);
      }, 3000);
    }
  }

  onVideoFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.videoFileName.set(file.name);
      this.isVideoUploading.set(true);
      const projId = this.getTargetProjectId();

      // Instant local video preview
      const localPreviewUrl = URL.createObjectURL(file);
      this.projectForm.patchValue({ videoUrl: localPreviewUrl });

      this.uploadFileToBackend(file, projId, (serverUrl) => {
        this.serverVideoUrl.set(serverUrl);
        this.projectForm.patchValue({ videoUrl: serverUrl });
        this.isVideoUploading.set(false);
      });

      setTimeout(() => {
        this.isVideoUploading.set(false);
      }, 3000);
    }
  }

  async onGalleryFileSelected(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      this.isGalleryUploading.set(true);
      const projId = this.getTargetProjectId();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const localPreviewUrl = URL.createObjectURL(file);
        const curIndex = this.galleryList().length;
        this.galleryList.update(list => [...list, localPreviewUrl]);

        this.uploadFileToBackend(file, projId, (serverUrl) => {
          this.galleryList.update(list => {
            const updated = [...list];
            if (updated[curIndex] === localPreviewUrl) {
              updated[curIndex] = serverUrl;
            } else {
              updated.push(serverUrl);
            }
            return updated;
          });
        });
      }

      setTimeout(() => {
        this.isGalleryUploading.set(false);
      }, 2500);
    }
  }

  // Tag list helpers
  addFeature() {
    const val = this.projectForm.get('newFeatureInput')?.value?.trim();
    if (val) {
      this.featuresList.update(f => [...f, val]);
      this.projectForm.patchValue({ newFeatureInput: '' });
    }
  }

  removeFeature(index: number) {
    this.featuresList.update(f => f.filter((_, i) => i !== index));
  }

  addMaterial() {
    const val = this.projectForm.get('newMaterialInput')?.value?.trim();
    if (val) {
      this.materialsList.update(m => [...m, val]);
      this.projectForm.patchValue({ newMaterialInput: '' });
    }
  }

  removeMaterial(index: number) {
    this.materialsList.update(m => m.filter((_, i) => i !== index));
  }

  addGalleryImage() {
    const val = this.projectForm.get('newGalleryInput')?.value?.trim();
    if (val) {
      this.galleryList.update(g => [...g, transformMediaUrl(val)]);
      this.projectForm.patchValue({ newGalleryInput: '' });
    }
  }

  removeGalleryImage(index: number) {
    this.galleryList.update(g => g.filter((_, i) => i !== index));
  }

  saveProject() {
    if (this.isCoverUploading() || this.isVideoUploading() || this.isGalleryUploading()) {
      alert('Media file upload is currently in progress. Please wait a moment before saving.');
      return;
    }

    if (this.projectForm.invalid) {
      alert('Please fill in all required fields marked with *');
      return;
    }

    const formVal = this.projectForm.value;

    // Determine final non-blob cover image
    let finalImage = this.serverCoverUrl();
    if (!finalImage || finalImage.startsWith('blob:')) {
      finalImage = formVal.image;
    }
    if (!finalImage || finalImage.startsWith('blob:')) {
      finalImage = '/assets/images/hero_home.png';
    }

    // Determine final non-blob video URL
    let finalVideoUrl: string | undefined = this.serverVideoUrl();
    if (!finalVideoUrl || finalVideoUrl.startsWith('blob:')) {
      finalVideoUrl = formVal.videoUrl;
    }
    if (!finalVideoUrl || finalVideoUrl.startsWith('blob:')) {
      finalVideoUrl = undefined;
    }

    // Clean gallery without temporary blob URLs
    const finalGallery = this.galleryList().filter(img => !img && !img.startsWith('blob:'));

    const catLabels: Record<string, string> = {
      'custom': 'Custom Residence',
      'rebuild': 'Knock Down & Rebuild',
      'duplexDesigns': 'Duplex Residence'
    };

    const projectData: Project = {
      id: this.editingProjectId() || pregId(formVal.title),
      title: formVal.title,
      category: formVal.category,
      categoryLabel: catLabels[formVal.category] || 'Custom Residence',
      location: formVal.location,
      suburb: formVal.suburb,
      image: finalImage,
      description: formVal.description,
      fullOverview: formVal.fullOverview,
      buildTime: formVal.buildTime,
      size: formVal.size,
      completionYear: formVal.completionYear,
      architect: formVal.architect,
      features: this.featuresList(),
      materials: this.materialsList(),
      galleryImages: finalGallery,
      videoUrl: finalVideoUrl,
      featured: true
    };

    this.projectService.saveProject(projectData);
    this.closeModal();
  }
}

function pregId(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
}
